import { useRouter } from "next/router"
import { useCallback, useEffect, useState } from "react"
import pickMatchingParams from "../../lib/utils/pickMatchingParams"
import { isEqual } from "lodash"

type UseUrlSyncStateOptions<SyncState> = {
  direction: "push" | "pull"
  params: SyncState
}

type UseUrlSyncStateReturn<SyncState> = readonly [
  SyncState,
  (syncingState: SyncState) => void
]

/**
 * URL query parameter와 동기화된 state를 관리하는 hook
 * @param direction 동기화 방식 - "push" | "pull"
 * @param params 객체 형태의 기본값
 * @returns [syncedState, setSyncState, { isError }] 형태의 튜플 - useState와 유사
 */
export function useUrlSyncState<
  SyncState extends Record<string, string | string[]>
>({
  direction = "push",
  params,
}: UseUrlSyncStateOptions<SyncState>): UseUrlSyncStateReturn<SyncState> {
  const [syncedState, setSyncState] = useState<SyncState>(params)
  const router = useRouter()

  const setSyncStateIfChanged = useCallback(
    (syncingState: SyncState) => {
      setSyncState((currentSyncedState) => {
        /** 이미 동기화된 상태라면 종료 */
        if (isEqual(currentSyncedState, syncingState)) {
          return currentSyncedState
        }

        return syncingState
      })
    },
    [params]
  )

  useEffect(() => {
    /** url params가 존재하지 않는다면 기본값으로 초기화 */
    const isEmptyUrlParams = Object.keys(router.query).length === 0
    if (isEmptyUrlParams) {
      setSyncStateIfChanged(params)
      return
    }
  }, [router.query])

  if (direction === "pull") {
    useUrlPullEffect({
      params,
      setSyncStateIfChanged,
    })
    return [syncedState, setSyncStateIfChanged]
  }

  useUrlPushEffect({
    syncedState,
    params,
    setSyncStateIfChanged,
  })

  return [syncedState, setSyncStateIfChanged]
}

type BaseSyncDirection<SyncState> = {
  params: SyncState
  setSyncStateIfChanged: (syncingState: SyncState) => void
}

type UseUrlPushStateOptions<
  SyncState extends Record<string, string | string[]>
> = BaseSyncDirection<SyncState> & {
  syncedState: SyncState
}

/** state의 값을 url의 params 값으로 동기화하는 hook - push 방식 */
function useUrlPushEffect<SyncState extends Record<string, string | string[]>>({
  syncedState,
  params,
  setSyncStateIfChanged,
}: UseUrlPushStateOptions<SyncState>) {
  const router = useRouter()
  const syncedStateKeys = Object.keys(syncedState)

  /** URL query parameter에서 state 초기화 */
  useEffect(() => {
    if (!router.isReady) {
      return
    }

    /** push 하고자 하는 key들을 url에서 추출한다. */
    const matchedParams = pickMatchingParams<SyncState>(params, router.query)

    if (!matchedParams) {
      return
    }

    setSyncStateIfChanged(matchedParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  /** state 변경 시 URL 동기화 */
  useEffect(() => {
    if (!router.isReady) {
      return
    }

    if (!syncedState) {
      return
    }

    const syncedQueryParams = { ...router.query }

    syncedStateKeys.forEach((key) => {
      if (syncedState[key]) {
        syncedQueryParams[key] = syncedState[key]
        return
      }

      /** queryParams에 없는 key url에서 제거 */
      if (!syncedState[key] && syncedQueryParams[key]) {
        delete syncedQueryParams[key]
      }
    })

    /** URL 업데이트 (페이지 리로드 없음) */
    router.replace(
      {
        pathname: router.pathname,
        query: { ...syncedQueryParams },
      },
      undefined,
      { shallow: true }
    )
  }, [syncedState])
}

/** url의 params 값을 SyncState 객체의 key값으로 추출하여 state로 동기화하는 hook - pull 방식 */
function useUrlPullEffect<SyncState extends Record<string, string | string[]>>({
  params,
  setSyncStateIfChanged,
}: BaseSyncDirection<SyncState>) {
  const router = useRouter()

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    const matchedParams = pickMatchingParams<SyncState>(params, router.query)

    if (!matchedParams) {
      return
    }

    setSyncStateIfChanged(matchedParams)
  }, [router.query])
}
